import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import StampImage from '../../../components/StampImage';
import { getSubjectName } from '../../../utils/subjectUtils';

interface Stamp {
  subject: string;
  count: number;
  teacherId?: string;
  updatedAt: any;
}

interface Student {
  stamps: Stamp[];
  classId: string;
}

interface Teacher {
  stampImageUrl?: string;
}

const StudentStamp: React.FC = () => {
  const { currentUser } = useAuth();
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [currentPages, setCurrentPages] = useState<{ [key: string]: number }>({});
  const [subjects, setSubjects] = useState<string[]>([]);
  const [studentData, setStudentData] = useState<Student | null>(null);
  const [teacherStamps, setTeacherStamps] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser || currentUser.role !== 'student') {
          console.error('학생이 아닌 사용자입니다.');
          return;
        }

        console.log('현재 사용자:', currentUser);

        if (!currentUser?.uid) {
          throw new Error('학생 정보를 찾을 수 없습니다.');
        }
        const studentDoc = await getDoc(doc(db, `schools/${currentUser.schoolId}/classes/${currentUser.classId}/students`, currentUser.uid));
        if (!studentDoc.exists()) {
          console.error('학생 정보를 찾을 수 없습니다.');
          return;
        }

        const studentData = studentDoc.data();
        console.log('학생 데이터:', studentData);

        const convertedStamps = studentData.stamps?.map((stamp: any) => {
          console.log('변환 전 도장 데이터:', stamp);
          const converted = {
            subject: stamp.subjectId || '미분류',
            count: stamp.stampCount || 0,
            teacherId: stamp.teacherId,
            updatedAt: stamp.updatedAt || new Date()
          };
          console.log('변환 후 도장 데이터:', converted);
          return converted;
        }) || [];

        const student = {
          ...studentData,
          stamps: convertedStamps
        } as Student;

        setStudentData(student);

        const uniqueSubjects = Array.from(new Set(
          student.stamps
            .map(stamp => stamp.subject)
            .filter((subject): subject is string => !!subject)
        ));
        console.log('과목 목록:', uniqueSubjects);
        setSubjects(uniqueSubjects);

        const teacherIds = Array.from(new Set(student.stamps.map(stamp => stamp.teacherId).filter((id): id is string => !!id)));
        const teacherStampsData: { [key: string]: string } = {};

        for (const teacherId of teacherIds) {
          const teacherDoc = await getDoc(doc(db, `schools/${currentUser.schoolId}/teachers`, teacherId));
          if (teacherDoc.exists()) {
            const teacher = teacherDoc.data() as Teacher;
            teacherStampsData[teacherId] = teacher.stampImageUrl || '/stamp.png';
          }
        }
        setTeacherStamps(teacherStampsData);

        if (uniqueSubjects.length > 0) {
          setSelectedSubject(uniqueSubjects[0]);
        }
      } catch (error) {
        console.error('데이터 로딩 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleTabChange = (index: number) => {
    console.log('선택된 과목:', subjects[index]);
    setSelectedSubject(subjects[index]);
  };

  const currentStamps = selectedSubject && studentData
    ? studentData.stamps.filter(stamp => stamp.subject === selectedSubject)
    : [];
  console.log('현재 도장:', currentStamps);
  const currentPage = selectedSubject ? currentPages[selectedSubject] || 1 : 1;
  const totalPages = Math.ceil(currentStamps.length / 10);

  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, value: number) => {
    if (selectedSubject) {
      setCurrentPages(prev => ({
        ...prev,
        [selectedSubject]: value
      }));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 'var(--gap-level-4)', color: 'var(--Color-text-normal)' }}>
        로딩 중...
      </div>
    );
  }

  const totalStamps = currentStamps.reduce((sum, stamp) => sum + stamp.count, 0);
  const selectedSubjectIndex = subjects.findIndex(subject => subject === selectedSubject);

  const stampImages: { teacherId?: string }[] = [];
  currentStamps.forEach(stamp => {
    for (let i = 0; i < stamp.count; i++) {
      stampImages.push({ teacherId: stamp.teacherId });
    }
  });

  const ITEMS_PER_PAGE = 10;
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedStampImages = stampImages.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 500,
          lineHeight: '36px',
          marginBottom: '24px',
          color: 'var(--Color-text-strong)',
        }}
      >
        도장 현황
      </div>

      {subjects.length > 0 ? (
        <>
          <div
            style={{
              display: 'flex',
              gap: 'var(--gap-level-2)',
              marginBottom: '24px',
              borderBottom: '1px solid var(--Color-line-normal)',
            }}
          >
            {subjects.map((subject, index) => (
              <button
                key={`subject-tab-${index}`}
                type="button"
                onClick={() => handleTabChange(index)}
                style={{
                  padding: '12px 16px',
                  border: 'none',
                  borderBottom: selectedSubjectIndex === index ? '2px solid var(--Color-fill-brand-color)' : '2px solid transparent',
                  background: 'none',
                  fontSize: '14px',
                  fontWeight: selectedSubjectIndex === index ? 600 : 400,
                  color: selectedSubjectIndex === index ? 'var(--Color-fill-brand-color)' : 'var(--Color-text-normal)',
                  cursor: 'pointer',
                }}
              >
                {getSubjectName(subject)}
              </button>
            ))}
          </div>

          {selectedSubject && (
            <div
              style={{
                padding: '24px',
                marginBottom: '24px',
                backgroundColor: 'var(--Color-background-normal)',
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong)',
                  }}
                >
                  {getSubjectName(selectedSubject)}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '14px',
                    color: 'var(--Color-text-normal)',
                    marginBottom: '8px',
                  }}
                >
                  현재 도장 수: {totalStamps}개
                </div>
                <div
                  style={{
                    height: '8px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--Color-line-normal)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min((totalStamps / 20) * 100, 100)}%`,
                      backgroundColor: 'var(--Color-fill-brand-color)',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: 'var(--gap-level-4)',
                  padding: '16px',
                  backgroundColor: 'var(--Color-fill-hover)',
                  borderRadius: '8px',
                }}
              >
                {paginatedStampImages.map((stamp, i) => (
                  <StampImage
                    key={startIdx + i}
                    count={1}
                    size={80}
                    imageUrl={
                      stamp.teacherId && teacherStamps[stamp.teacherId]
                        ? teacherStamps[stamp.teacherId]
                        : '/stamp.png'
                    }
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '24px',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => handlePageChange(e, currentPage - 1)}
                    disabled={currentPage <= 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--Color-line-normal)',
                      borderRadius: '4px',
                      background: 'var(--Color-background-normal)',
                      cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage <= 1 ? 0.5 : 1,
                    }}
                  >
                    이전
                  </button>
                  <span
                    style={{
                      fontSize: '14px',
                      color: 'var(--Color-text-normal)',
                    }}
                  >
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handlePageChange(e, currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--Color-line-normal)',
                      borderRadius: '4px',
                      background: 'var(--Color-background-normal)',
                      cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage >= totalPages ? 0.5 : 1,
                    }}
                  >
                    다음
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            padding: '24px',
            marginBottom: '24px',
            backgroundColor: 'var(--Color-background-normal)',
            borderRadius: '8px',
            border: '1px solid var(--Color-line-normal)',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              color: 'var(--Color-text-normal)',
              textAlign: 'center',
            }}
          >
            아직 받은 도장이 없습니다.
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentStamp;
